// FIRST, before every other import (T69): it registers the `error` and
// `unhandledrejection` listeners, and ES modules evaluate imports in order,
// so an error thrown while any module below initialises is reported too.
import "./app/error-reporting/early";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app";
import { ErrorBoundary } from "./app/error-reporting/error-boundary";
import { registerPwa } from "./app/pwa/register";
import { holdBootSplash, startSystemChrome } from "./app/system-chrome";
import { AppFrame } from "./app-frame";
import { captureInviteLanding } from "./features/invite/landing";
import { capturePlanLanding } from "./features/plan-share/landing";
import { captureShareLanding } from "./features/share/landing";
import { startUpdater } from "./native";
import "./index.css";

/**
 * Work that must finish before the first render. Error reporting is already
 * installed by the first import above.
 *
 * Boot steps land here IN THIS ORDER:
 *   1. T70: `notifyAppReady()` — first, so a bundle that boots is never
 *      rolled back for a later step's failure.
 *   2. T115: the orientation lock.
 *   3. T71: service-worker registration (web only).
 *   4. D260: a share link's `?ref=` is kept and reported, and taken out of
 *      the address before the router reads it.
 */
async function boot(): Promise<void> {
  // T70: `notifyAppReady()` first, then a bundle queued by an earlier launch
  // is applied (the page reloads and nothing renders). The manifest check
  // runs in the background and is NOT awaited here. Web: a no-op.
  await startUpdater();
  // T115: portrait on phones. After the updater, because a launch that
  // reloads to apply a bundle would throw this work away; synchronous, and it
  // never throws.
  startSystemChrome();
  // T71: the service worker, web build only (a no-op on the device and in
  // `--mode native`). Not awaited: registration waits for `load` and must
  // never hold the first render.
  registerPwa();
  // D260: synchronous, never throws past here — a landing that cannot be
  // recorded is one missing funnel row, never a blank app.
  try {
    captureShareLanding();
  } catch (error) {
    console.warn("[share] the landing was not recorded", error);
  }
  // D273: a plan link's id, kept until the person is signed in. Reads and
  // keeps; opens nothing, imports nothing, generates nothing (T-145).
  try {
    capturePlanLanding();
  } catch (error) {
    console.warn("[plan-share] the plan link was not kept", error);
  }
  // D275: an invite link's code, kept until the person has an account.
  try {
    captureInviteLanding();
  } catch (error) {
    console.warn("[invite] the invite was not kept", error);
  }
}

const root = document.getElementById("root");
if (!root) {
  throw new Error("index.html has no #root element");
}

boot()
  .catch((error: unknown) => {
    // A failed boot step must not blank the app: render anyway.
    console.error("boot failed", error);
  })
  .finally(() => {
    // T115: `launchAutoHide` is off, so nothing else takes the splash down.
    // It stays until the session gate has decided (`D194`), which releases
    // it; a boot that never gets there is covered by the safety timer. Held
    // BEFORE the render, so the gate's release can never come first.
    holdBootSplash();
    createRoot(root).render(
      <StrictMode>
        <AppFrame>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </AppFrame>
      </StrictMode>
    );
  });
